import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Alert, Button, Spinner } from 'react-bootstrap'
import FeatherIcon from 'feather-icons-react'
import './Auto.scss'
import {
  DebugFieldVisibilityProvider,
  useDebugFieldVisibility,
} from './index'

const CONTROL_SELECTOR = 'input:not([type="hidden"]):not([data-auto-debug-field]), textarea, select, [role="combobox"], [role="button"][aria-haspopup="listbox"]'
const GROUP_SELECTOR = '.form-group, .MuiFormControl-root, .ant-form-item'
const PAGINATION_SELECTOR = '[class*="MuiTablePagination-root"], [class*="MuiPagination-root"], [data-auto-debug-pagination], .dashboard-page-size-select'
const ACTION_EXCLUDE_SELECTOR = `${PAGINATION_SELECTOR}, thead, tbody, tfoot, nav, [role="tablist"], [aria-hidden="true"], [data-auto-debug-fields], .input-group, .react-datepicker, .MuiPickersPopper-root`
const LEGACY_DEBUG_CONTROL_SELECTOR = [
  'input[type="checkbox"][aria-label^="แสดง "]:not([data-auto-debug-field])',
  'input[type="checkbox"][title^="แสดง "]:not([data-auto-debug-field])',
  'input[type="checkbox"][aria-label^="แสดงแท็บ "]',
  'input[type="checkbox"][title^="แสดงแท็บ "]',
  'input[type="checkbox"][id^="medical-certificate-visible-"]',
  '[class*="debug-visibility"]:not([class*="wrap"]) > input[type="checkbox"]:not([data-auto-debug-field])',
  'button[title^="บันทึกการแสดงผลลง designer_visible_ui_option."]',
].join(', ')

const getLegacyDebugControls = (root) => [
  ...root.querySelectorAll(LEGACY_DEBUG_CONTROL_SELECTOR),
  ...Array.from(root.querySelectorAll('button')).filter((button) =>
    /^(บันทึกการตั้งค่า|บันทึกการแสดงผล)/.test(button.textContent.trim()),
  ),
]

const getTableCellColumns = (rows) => {
  const columns = new Map()
  let rowSpans = []
  Array.from(rows).forEach((row, rowIndex) => {
    let column = 0
    Array.from(row.cells).forEach((cell) => {
      while (Array.from({ length: cell.colSpan }).some((_, index) => rowSpans[column + index] > 0)) {
        column += 1
      }
      columns.set(cell, column)
      for (let index = 0; index < cell.colSpan; index += 1) {
        rowSpans[column + index] = Math.max(rowSpans[column + index] || 0, cell.rowSpan || rows.length - rowIndex)
      }
      column += cell.colSpan
    })
    rowSpans = rowSpans.map((span) => Math.max(span - 1, 0))
  })
  return columns
}

const getFieldsWithIdentifiers = (fields) => {
  const candidates = fields.map((field) => {
    const { key, label, element, labelElement } = field
    if (key.startsWith('tab:')) return [key]
    const isTableOrButton = key.startsWith('table:') || key.startsWith('button:')
    const controls = isTableOrButton ? [] : Array.from(element.querySelectorAll(CONTROL_SELECTOR))
    const names = [...new Set(controls.map((control) => control.getAttribute('name')).filter(Boolean))]
    const designerName = labelElement.getAttribute('name')
    const identifierName = isTableOrButton ? element.getAttribute('name') :
      designerName?.startsWith('label-') ? designerName.slice('label-'.length) :
        names.length === 1 ? names[0] : element.getAttribute('name')
    const identifierId = isTableOrButton ? element.id :
      controls.length === 1 ? controls[0].id : element.id
    const prefixIndex = isTableOrButton ? key.lastIndexOf(`:${label}`) : -1
    const prefix = prefixIndex >= 0 ? key.slice(0, prefixIndex + 1) :
      key.startsWith('group:') ? 'group:' : key.startsWith('radio:') ? 'radio:' : ''
    return [identifierName, identifierId && !/^(mui-\d+|react-select-\d+-input)$/.test(identifierId)
      ? identifierId : null].filter(Boolean).map((identifier) => `${prefix}${identifier}`).concat(key)
  })
  return fields.map((field, index) => {
    const key = candidates[index].find((candidate) =>
      candidates.filter((items) => items.includes(candidate)).length === 1,
    ) || field.key
    if (key === field.key) return field
    return { ...field, key, legacyKey: field.legacyKey || field.key }
  })
}

export const discoverFields = (root) => {
  if (!root) return []
  const counts = {}
  const groups = Array.from(root.querySelectorAll('[data-auto-debug-group]')).reduce((fields, element) => {
    const label = element.getAttribute('data-auto-debug-group')?.trim()
    if (!label || !element.parentElement || fields.some((field) => field.element.contains(element))) return fields
    const keyBase = `group:${label}`
    counts[keyBase] = (counts[keyBase] || 0) + 1
    fields.push({
      key: counts[keyBase] === 1 ? keyBase : `${keyBase}:${counts[keyBase]}`,
      label,
      element,
      labelElement: element,
      mountParent: element.parentElement,
      mountBefore: element,
    })
    return fields
  }, [])
  const fields = Array.from(root.querySelectorAll('label')).reduce((fields, labelElement) => {
    if (labelElement.closest(PAGINATION_SELECTOR) || fields.some((field) => field.element.contains(labelElement))) return fields
    const designerName = labelElement.getAttribute('name')
    const isDesignerField = designerName?.startsWith('label-')
    const labelText = labelElement.textContent.replace(/\*/g, '').trim()
    if ((!labelText && !isDesignerField) || labelText.startsWith('แสดง ')) return fields
    let element = labelElement.closest(GROUP_SELECTOR)
    if (!element && isDesignerField) {
      element = labelElement.closest('fieldset, .MuiGrid-item') || labelElement.parentElement
    }
    if (!element) {
      const parent = labelElement.parentElement
      if (parent?.querySelector(CONTROL_SELECTOR)) element = parent
      else if (parent?.parentElement?.querySelector(CONTROL_SELECTOR)) {
        element = parent.parentElement
      }
    }
    if (!element || !root.contains(element)) {
      return fields
    }
    const controls = Array.from(element.querySelectorAll(CONTROL_SELECTOR))
    if (!controls.length && !isDesignerField) return fields
    const designerLabels = Array.from(element.querySelectorAll('label[name^="label-"]'))
    if (designerLabels.length && !isDesignerField) return fields
    const labels = (designerLabels.length ? designerLabels : Array.from(element.querySelectorAll('label'))).filter((item) =>
      item.textContent.replace(/\*/g, '').trim() &&
      !(item.classList.contains('form-check-label') && item.closest('.form-check')?.querySelector('input[type="radio"]') &&
        element.querySelector('label.form-label')) &&
      !controls.some((control) => control.type === 'radio' && control.id && control.id === item.htmlFor),
    )
    if (labels.length > 1 || (labels[0] || designerLabels[0]) !== labelElement) return fields
    if (fields.some((field) => field.element === element)) return fields

    const fieldName = isDesignerField ? designerName.slice('label-'.length) : ''
    const fieldId = isDesignerField ? labelElement.id.replace(/^label-/, '') : ''
    const optionLabels = Array.from(element.querySelectorAll('.form-check-label'))
      .map((item) => item.textContent.trim()).filter(Boolean)
    const label = labelText || optionLabels.join(' / ') || fieldName
    const keyBase = labelText || fieldName
    const associatedControls = controls.filter((control) =>
      control.name === fieldName ||
      control.name === fieldId ||
      control.id === fieldId ||
      control.id.startsWith(`radio-${fieldId}-`),
    )
    const fieldControls = associatedControls.length ? associatedControls : controls
    const checkbox = controls.find((control) => fieldId && control.type === 'checkbox' &&
      (control.id === fieldId || control.id === `checkbox-${fieldId}`)) ||
      (fieldControls.length === 1 && fieldControls[0].type === 'checkbox' ? fieldControls[0] : null)
    const firstOption = !labelText && fieldControls.length && fieldControls.every((control) =>
      ['checkbox', 'radio'].includes(control.type),
    ) ? fieldControls[0] : null
    const optionContainer = firstOption?.type === 'radio' && firstOption.parentElement?.classList.contains('form-check')
      ? firstOption.parentElement : null
    const buttonLabelBox = fieldId && Array.from(element.querySelectorAll('button')).some((button) => button.id === fieldId)
      ? labelElement.parentElement : null
    counts[keyBase] = (counts[keyBase] || 0) + 1
    fields.push({
      key: counts[keyBase] === 1 ? keyBase : `${keyBase}:${counts[keyBase]}`,
      label,
      element,
      labelElement,
      optionContainer,
      buttonLabelBox,
      mountParent: optionContainer?.parentElement || (checkbox || firstOption)?.parentElement || labelElement,
      mountBefore: optionContainer || checkbox || firstOption || labelElement.firstChild,
    })
    return fields
  }, groups)
  Array.from(root.querySelectorAll('input[type="radio"]')).forEach((radio) => {
    if (radio.closest(PAGINATION_SELECTOR) || fields.some((field) => field.element.contains(radio))) return
    const group = radio.closest('[role="radiogroup"], fieldset, .form-group') || radio.parentElement?.parentElement
    if (!group || group === root || !group.parentElement || fields.some((field) => field.element.contains(group))) return
    const radios = Array.from(group.querySelectorAll('input[type="radio"]'))
    if (radios.length < 2 || radios[0] !== radio) return
    const label = group.getAttribute('aria-label') || group.querySelector('legend')?.textContent.trim() ||
      Array.from(group.querySelectorAll('label')).map((item) => item.textContent.trim()).filter(Boolean).join(' / ')
    if (!label) return
    const keyBase = `radio:${label}`
    counts[keyBase] = (counts[keyBase] || 0) + 1
    fields.push({
      key: counts[keyBase] === 1 ? keyBase : `${keyBase}:${counts[keyBase]}`,
      label,
      element: group,
      labelElement: group,
      mountParent: group.parentElement,
      mountBefore: group,
    })
  })
  Array.from(root.querySelectorAll(GROUP_SELECTOR)).forEach((group) => {
    if (group.closest(PAGINATION_SELECTOR) || group.querySelector('label') || fields.some((field) => field.element.contains(group))) return
    const controls = Array.from(group.querySelectorAll(CONTROL_SELECTOR))
    if (!controls.length || !group.parentElement || group.parentElement.children.length !== 2) return
    const heading = group.previousElementSibling
    const label = heading?.textContent.trim()
    if (!label || label.startsWith('แสดง ')) return
    const keyBase = controls[0].name || label
    counts[keyBase] = (counts[keyBase] || 0) + 1
    fields.push({
      key: counts[keyBase] === 1 ? keyBase : `${keyBase}:${counts[keyBase]}`,
      label,
      element: group.parentElement,
      labelElement: heading,
      mountParent: heading,
      mountBefore: heading.firstChild,
    })
  })
  Array.from(root.querySelectorAll(CONTROL_SELECTOR)).forEach((control) => {
    const parentCombo = control.closest('[role="combobox"]')
    if (
      control.closest(PAGINATION_SELECTOR) ||
      control.closest('[data-auto-debug-table]') ||
      (control.closest('thead') && control.type === 'checkbox') ||
      control.closest(GROUP_SELECTOR) ||
      (parentCombo && parentCombo !== control) ||
      control.getAttribute('aria-hidden') === 'true' ||
      control.closest('[data-auto-debug-checkbox]') ||
      fields.some((field) => field.element.contains(control))
    ) return
    const cell = control.closest('td')
    const column = cell?.closest('table')?.querySelector('thead tr')?.children[cell.cellIndex]
    const fieldColumn = cell ? null : control.closest('[class*="col-"]')
    const heading = fieldColumn?.firstElementChild?.tagName === 'SMALL' &&
      fieldColumn.firstElementChild.textContent.trim() ? fieldColumn.firstElementChild : null
    const label = column?.textContent.trim() ||
      control.getAttribute('aria-label') ||
      control.getAttribute('placeholder') ||
      control.getAttribute('name') ||
      control.id
    if (!label && !heading) return
    const dateGroup = (control.classList.contains('input-date') || control.type === 'date') &&
      control.closest('.MuiBox-root, .react-datepicker-wrapper')
    const controlGroup = control.closest('.input-group, .form-select, [class*="MuiFormControl-root"]') ||
      dateGroup || control.parentElement
    const standaloneWrapper = controlGroup?.parentElement?.children.length === 1 &&
      !controlGroup.parentElement.matches('[class*="col-"], .form-group') ? controlGroup.parentElement : null
    const element = cell || (heading && fieldColumn) || standaloneWrapper || controlGroup
    if (!element || fields.some((field) => field.element === element)) return
    const keyBase = control.getAttribute('name') || control.id || label || heading.textContent.trim()
    counts[keyBase] = (counts[keyBase] || 0) + 1
    fields.push({
      key: counts[keyBase] === 1 ? keyBase : `${keyBase}:${counts[keyBase]}`,
      label: heading ? heading.textContent.trim() : label,
      element,
      labelElement: heading || control,
      mountParent: heading || (cell ? element : element.parentElement || element),
      mountBefore: heading ? heading.firstChild : cell ? element.firstChild : element,
    })
  })
  const panes = Array.from(root.querySelectorAll('[id^="tab-pane-"]'))
  Array.from(root.querySelectorAll('a.nav-link[data-rb-event-key]')).forEach((tabLink) => {
    const tabKey = tabLink.getAttribute('data-rb-event-key')
    const element = tabLink.parentElement
    const label = tabLink.textContent.trim()
    if (!tabKey || !label || element?.id !== `tab-nav-${tabKey}`) return
    const pane = panes.find((item) => item.id === `tab-pane-${tabKey}`)
    fields.push({
      key: `tab:${tabKey}`,
      label,
      element,
      relatedElements: pane ? [pane] : [],
      tabLink,
      labelElement: tabLink,
      mountParent: element,
      mountBefore: tabLink,
    })
  })
  Array.from(root.querySelectorAll('[role="tab"][aria-controls]')).forEach((tabLink) => {
    const tabKey = tabLink.getAttribute('aria-controls')
    const label = tabLink.textContent.trim()
    if (!tabKey || !label || fields.some((field) => field.element === tabLink)) return
    const pane = panes.find((item) => item.id === tabKey)
    fields.push({
      key: `tab:${tabKey.replace(/^tab-pane-/, '')}`,
      label,
      element: tabLink,
      relatedElements: pane ? [pane] : [],
      tabLink,
      labelElement: tabLink,
      mountParent: tabLink.parentElement,
      mountBefore: tabLink,
    })
  })
  Array.from(root.querySelectorAll('[role="tab"], a.nav-link[data-rb-event-key]')).forEach((tabLink) => {
    if (fields.some((field) => field.element === tabLink || field.tabLink === tabLink)) return
    const label = tabLink.textContent.trim()
    if (!label || !tabLink.parentElement || tabLink.closest(PAGINATION_SELECTOR)) return
    const tabKey = tabLink.getAttribute('aria-controls') || tabLink.getAttribute('data-rb-event-key') || label
    const pane = panes.find((item) => item.id === tabKey || item.id === `tab-pane-${tabKey}`)
    fields.push({
      key: `tab:${tabKey.replace(/^tab-pane-/, '')}`,
      label,
      element: tabLink,
      relatedElements: pane ? [pane] : [],
      tabLink,
      labelElement: tabLink,
      mountParent: tabLink.parentElement,
      mountBefore: tabLink,
    })
  })
  const tables = Array.from(root.querySelectorAll('table'))
  const editableTables = new Map(tables.map((table) => [table,
    Array.from(table.querySelectorAll(CONTROL_SELECTOR)).some((control) => control.closest('tbody')),
  ]))
  const tableColumns = new Map(tables.map((table) => [table, {
    headings: getTableCellColumns(table.tHead?.rows || []),
    cells: Array.from(table.tBodies).reduce((columns, body) => {
      getTableCellColumns(body.rows).forEach((column, cell) => columns.set(cell, column))
      return columns
    }, new Map()),
  }]))
  Array.from(root.querySelectorAll('table thead th')).forEach((heading) => {
    const table = heading.closest('table')
    const hasEditableBody = editableTables.get(table)
    const title = heading.closest('[data-auto-debug-table]')?.getAttribute('data-auto-debug-table') ||
      (table.querySelector('tbody') && !hasEditableBody && !table.closest(PAGINATION_SELECTOR) &&
        `${tables.indexOf(table) + 1}`)
    const label = heading.textContent.trim() || heading.getAttribute('aria-label') ||
      heading.querySelector('[aria-label]')?.getAttribute('aria-label')
    if (!title || !label || heading.colSpan > 1 || fields.some((field) => field.element === heading)) return
    const headingContent = Array.from(heading.children).find((child) => child.hasAttribute('data-rbd-draggable-id'))
    const columnIndex = tableColumns.get(table)?.headings.get(heading)
    if (columnIndex === undefined) return
    const relatedElements = Array.from(tableColumns.get(table).cells).filter(([cell, column]) =>
      column === columnIndex && cell.colSpan === 1,
    ).map(([cell]) => cell)
    fields.push({
      key: `table:${title}:${label}`,
      label,
      element: heading,
      relatedElements,
      labelElement: heading,
      mountParent: headingContent || heading,
      mountBefore: headingContent ? headingContent.firstChild : heading.firstChild,
    })
  })
  Array.from(root.querySelectorAll('button')).forEach((button) => {
    const label = button.getAttribute('aria-label') || button.getAttribute('title') || button.textContent.trim()
    if (!label || button.closest(ACTION_EXCLUDE_SELECTOR) || button.classList.contains('close') ||
      fields.some((field) => field.element.contains(button))) return
    const heading = button.closest('.card-body, .modal-body')?.querySelector('h5')?.textContent.trim()
    const keyBase = `button:${heading || ''}:${label}`
    counts[keyBase] = (counts[keyBase] || 0) + 1
    fields.push({
      key: counts[keyBase] === 1 ? keyBase : `${keyBase}:${counts[keyBase]}`,
      label,
      element: button,
      labelElement: button,
      mountParent: button.parentElement,
      mountBefore: button,
    })
  })
  return getFieldsWithIdentifiers(fields)
}

export const discoverModalFields = (modal) => {
  const rows = Array.from(modal.querySelectorAll('.modal-body .row')).reduce((fields, row) => {
    const [heading, ...contents] = Array.from(row.children)
    const label = heading?.textContent.trim()
    if (!label || !heading.className.includes('col-') || heading.querySelector(CONTROL_SELECTOR) ||
      !contents.some((content) => content.querySelector(`${CONTROL_SELECTOR}, button`))) return fields
    fields.push({
      key: label,
      label,
      element: row,
      labelElement: heading,
      mountParent: heading,
      mountBefore: heading.firstChild,
      modalRow: true,
    })
    return fields
  }, [])
  return getFieldsWithIdentifiers([
    ...rows,
    ...discoverFields(modal).filter(({ element, labelElement }) =>
      !rows.some((row) => row.element.contains(element) || row.element.contains(labelElement)),
    ),
  ])
}

const AutoDebugFieldContent = ({ children, onFieldsChange }) => {
  const rootRef = useRef(null)
  const fieldsRef = useRef([])
  const originalsRef = useRef(new Map())
  const redirectedTabsRef = useRef(new WeakSet())
  const mountsRef = useRef(new Map())
  const modalRootsRef = useRef(new Set())
  const visibility = useDebugFieldVisibility()
  const visibilityRef = useRef(visibility)
  visibilityRef.current = visibility
  const [fields, setFields] = useState([])
  const [hasFields, setHasFields] = useState(null)
  const [pageTitle, setPageTitle] = useState('แบบฟอร์ม')
  const [showFieldKeys, setShowFieldKeys] = useState(false)

  const restoreVisibility = (keepElements) => {
    originalsRef.current.forEach((original, element) => {
      if (keepElements?.has(element)) return
      element.removeAttribute('data-auto-debug-hidden')
      if (original.opacity) {
        element.style.setProperty('opacity', original.opacity, original.opacityPriority)
      } else element.style.removeProperty('opacity')
      originalsRef.current.delete(element)
    })
  }

  const applyVisibility = () => {
    fieldsRef.current.forEach(({ key, legacyKey, element, relatedElements = [] }) => {
      const visible = visibilityRef.current.isVisible(key, legacyKey)
      const hide = !visibilityRef.current.isDebugMode && !visible
      const dim = visibilityRef.current.isDebugMode && !visible
      const targets = [element, ...relatedElements]
      targets.forEach((target) => {
        if (!originalsRef.current.has(target)) {
          originalsRef.current.set(target, {
            opacity: target.style.getPropertyValue('opacity'),
            opacityPriority: target.style.getPropertyPriority('opacity'),
          })
        }
        const original = originalsRef.current.get(target)
        const opacity = dim ? '0.45' : original.opacity
        if (target.hasAttribute('data-auto-debug-hidden') !== hide) {
          target.toggleAttribute('data-auto-debug-hidden', hide)
        }
        if (target.style.getPropertyValue('opacity') !== opacity) {
          if (opacity) target.style.setProperty('opacity', opacity, original.opacityPriority)
          else target.style.removeProperty('opacity')
        }
      })
    })
    if (!visibilityRef.current.isDebugMode) {
      fieldsRef.current.forEach(({ key, legacyKey, element, tabLink }) => {
        if (!(tabLink?.classList.contains('active') || tabLink?.getAttribute('aria-selected') === 'true') ||
          visibilityRef.current.isVisible(key, legacyKey)) {
          redirectedTabsRef.current.delete(element)
          return
        }
        if (redirectedTabsRef.current.has(element)) return
        const nextTab = Array.from(element.parentElement.querySelectorAll('a.nav-link[data-rb-event-key], [role="tab"][aria-controls]'))
          .find((link) => !link.hasAttribute('data-auto-debug-hidden') &&
            !link.parentElement.hasAttribute('data-auto-debug-hidden'))
        if (nextTab) {
          redirectedTabsRef.current.add(element)
          nextTab.click()
        }
      })
    }
  }

  useEffect(() => {
    const root = rootRef.current
    if (!root) return undefined
    let frame = null
    const scan = () => {
      frame = null
      const title = Array.from(root.querySelectorAll('.card-header .card-title, .page-header h1, .page-header h2'))
        .map((heading) => heading.textContent.trim()).find(Boolean) || 'แบบฟอร์ม'
      setPageTitle((current) => current === title ? current : title)
      const modals = Array.from(document.querySelectorAll('.modal.show'))
        .filter((modal) => !root.contains(modal))
      const legacyControls = visibilityRef.current.isDebugMode
        ? getLegacyDebugControls(root) : []
      const legacyPanes = legacyControls.map((control) => control.closest('[id^="tab-pane-"]'))
      const useLegacyDebug = visibilityRef.current.isDebugMode && (
        legacyPanes.some((pane) => !pane) ||
        modals.some((modal) => getLegacyDebugControls(modal).length > 0)
      )
      const nextModalRoots = new Set(useLegacyDebug ? [] : modals)
      modalRootsRef.current.forEach((modal) => {
        if (!nextModalRoots.has(modal)) modal.classList.remove('auto-debug-field-scope')
      })
      nextModalRoots.forEach((modal) => modal.classList.add('auto-debug-field-scope'))
      modalRootsRef.current = nextModalRoots
      const nextFields = useLegacyDebug ? [] : [
        ...discoverFields(root).filter(({ element }) =>
          !legacyPanes.some((pane) => pane?.contains(element)),
        ),
        ...modals.flatMap((modal) => {
          const modalTitle = modal.querySelector('.modal-title')?.textContent.trim() || 'Modal'
          return discoverModalFields(modal).map((field) => ({
            ...field,
            key: `modal:${modalTitle}:${field.key}`,
            legacyKey: field.legacyKey ? `modal:${modalTitle}:${field.legacyKey}` : undefined,
          }))
        }),
      ]
      restoreVisibility(new Set(nextFields.flatMap(({ element, relatedElements = [] }) =>
        [element, ...relatedElements],
      )))
      mountsRef.current.forEach((mount, labelElement) => {
        if (!visibilityRef.current.isDebugMode || !nextFields.some((field) => field.labelElement === labelElement && field.mountParent === mount.parentElement)) {
          mount.parentElement?.classList.remove('auto-debug-tab-item', 'auto-debug-option-item', 'auto-debug-modal-label')
          labelElement.parentElement?.classList.remove('auto-debug-button-label-box')
          mount.remove()
          mountsRef.current.delete(labelElement)
        }
      })
      if (visibilityRef.current.isDebugMode) {
        nextFields.forEach(({ labelElement, mountParent, mountBefore, tabLink, optionContainer, buttonLabelBox, modalRow }) => {
          if (mountsRef.current.has(labelElement)) {
            if (mountBefore === labelElement && labelElement.tagName === 'BUTTON' &&
              mountParent.classList.contains('justify-content-between')) {
              mountsRef.current.get(labelElement).style.marginLeft = 'auto'
            }
            return
          }
          const mount = document.createElement('span')
          mount.setAttribute('data-auto-debug-checkbox', '')
          mountParent.insertBefore(mount, mountBefore)
          if (mountBefore === labelElement && labelElement.tagName === 'BUTTON' &&
            mountParent.classList.contains('justify-content-between')) mount.style.marginLeft = 'auto'
          if (tabLink) mountParent.classList.add('auto-debug-tab-item')
          if (optionContainer) mountParent.classList.add('auto-debug-option-item')
          if (buttonLabelBox) buttonLabelBox.classList.add('auto-debug-button-label-box')
          if (modalRow) mountParent.classList.add('auto-debug-modal-label')
          mountsRef.current.set(labelElement, mount)
        })
      }
      fieldsRef.current = nextFields
      setHasFields(nextFields.length > 0)
      onFieldsChange(nextFields.length > 0)
      setFields((current) => {
        const unchanged = current.length === nextFields.length && current.every((field, index) =>
          field.key === nextFields[index].key &&
          field.legacyKey === nextFields[index].legacyKey &&
          field.labelElement === nextFields[index].labelElement &&
          field.mount === mountsRef.current.get(field.labelElement),
        )
        return unchanged ? current : nextFields.map(({ key, legacyKey, label, labelElement }) => ({
          key,
          legacyKey,
          label,
          labelElement,
          mount: mountsRef.current.get(labelElement),
        }))
      })
      applyVisibility()
    }
    const scheduleScan = () => {
      if (frame === null) frame = window.setTimeout(scan, 0)
    }
    scan()
    const observer = new MutationObserver((mutations) => {
      if (mutations.some((mutation) => mutation.type !== 'characterData' ||
        mutation.target.parentElement?.closest('button, [role="tab"], label, th, small'))) scheduleScan()
    })
    observer.observe(root, {
      subtree: true,
      childList: true,
      characterData: true,
    })
    const modalObserver = new MutationObserver((mutations) => {
      if (mutations.some(({ target, addedNodes, removedNodes }) =>
        target.closest?.('.modal') ||
        [...addedNodes, ...removedNodes].some((node) => node.nodeType === 1 &&
          (node.matches('.modal') || node.querySelector('.modal'))),
      )) scheduleScan()
    })
    modalObserver.observe(document.body, { subtree: true, childList: true })
    return () => {
      observer.disconnect()
      modalObserver.disconnect()
      if (frame !== null) window.clearTimeout(frame)
      mountsRef.current.forEach((mount, labelElement) => {
        mount.parentElement?.classList.remove('auto-debug-tab-item', 'auto-debug-option-item', 'auto-debug-modal-label')
        labelElement.parentElement?.classList.remove('auto-debug-button-label-box')
        mount.remove()
      })
      mountsRef.current.clear()
      modalRootsRef.current.forEach((modal) => modal.classList.remove('auto-debug-field-scope'))
      modalRootsRef.current.clear()
      restoreVisibility()
    }
  }, [onFieldsChange, visibility.isDebugMode])

  useLayoutEffect(() => { applyVisibility() })

  const showContent = hasFields === false ||
    (hasFields === true && visibility.isConfigReady)

  return (
    <>
      <div ref={rootRef} className="auto-debug-field-scope" style={{ display: showContent ? 'contents' : 'none' }}>{children}</div>
      {showContent && visibility.isDebugMode && fields.map(({ key, legacyKey, label, mount }) => mount && createPortal(
        <input
          key={key}
          type="checkbox"
          data-auto-debug-field
          aria-label={`แสดง ${label}`}
          checked={visibility.isVisible(key, legacyKey)}
          onChange={(event) => visibility.setVisible(key, event.target.checked, legacyKey)}
        />,
        mount,
      ))}
      {showContent && visibility.isDebugMode && fields.length > 0 && (
        <div
          data-auto-debug-fields
          style={{
            backgroundColor: '#fff',
            border: '1px solid #4099ff',
            borderRadius: 5,
            bottom: 64,
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '10px 12px',
            position: 'fixed',
            right: 20,
            zIndex: 10400,
          }}
        >
          {visibility.error && <Alert variant="danger">{visibility.error}</Alert>}
          {showFieldKeys && (
            <div
              id="auto-debug-field-keys"
              role="region"
              aria-label="ชื่อ Field ใน Config"
              style={{
                backgroundColor: '#fff',
                border: '1px solid #4099ff',
                borderRadius: 5,
                bottom: 'calc(100% + 8px)',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
                maxHeight: 300,
                overflowY: 'auto',
                padding: '12px',
                position: 'absolute',
                right: 0,
                width: 'min(420px, calc(100vw - 40px))',
              }}
            >
              <strong className="d-block mb-2">ชื่อ Field ที่ใช้บันทึกใน Config</strong>
              <ul className="mb-0 pl-3">
                {fields.map(({ key, label }, index) => (
                  <li key={`${key}:${index}`} className="mb-2">
                    <span className="d-block">{label}</span>
                    <code style={{ overflowWrap: 'anywhere' }}>{key}</code>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div>
            <strong className="d-block">ตั้งค่าหน้า {pageTitle}</strong>
            <span className="small text-muted">ติ๊ก field ที่ต้องการแสดง</span>
          </div>
          <Button
            size="sm"
            variant="outline-primary"
            aria-controls="auto-debug-field-keys"
            aria-expanded={showFieldKeys}
            onClick={() => setShowFieldKeys((current) => !current)}
          >
            <FeatherIcon className="mr-1" icon="list" size={14} />
            {showFieldKeys ? 'ซ่อนชื่อ Field' : 'ดูชื่อ Field'}
          </Button>
          <Button
            size="sm"
            variant="primary"
            disabled={!visibility.hasChanges || visibility.isSaving}
            onClick={visibility.save}
          >
            {visibility.isSaving ? (
              <Spinner animation="border" className="mr-1" size="sm" />
            ) : (
              <FeatherIcon className="mr-1" icon="save" size={14} />
            )}
            {visibility.isSaving ? 'กำลังบันทึก...' : 'บันทึกการตั้งค่า'}
          </Button>
        </div>
      )}
    </>
  )
}

const AutoDebugFieldVisibility = ({ formKey, legacyFormKey, children }) => {
  const [hasFields, setHasFields] = useState(false)
  return (
    <DebugFieldVisibilityProvider
      formKey={formKey}
      legacyFormKey={legacyFormKey}
      showControls={false}
      enabled={hasFields}
      waitForConfig
    >
      <AutoDebugFieldContent onFieldsChange={setHasFields}>
        {children}
      </AutoDebugFieldContent>
    </DebugFieldVisibilityProvider>
  )
}

export default AutoDebugFieldVisibility
